import React, { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiArrowRight, FiBookOpen, FiUsers, FiUser } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import { useGetPublicStatsQuery } from "../../api/publicStatsApi";
import { WHATSAPP_COMMUNITY_URL } from "../../constants/community";
import communityIcon from "../../assets/icons/terakki-topluluk-128.png";
import PhoneMockup from "../../components/landing/PhoneMockup";
import StoreButtons from "../../components/landing/StoreButtons";
import CountUp from "../../components/landing/CountUp";
import useInView from "../../components/landing/useInView";

// Karşılama öğeleri sırayla belirir; her biri bir öncekinden biraz sonra.
const enter = (delay) => `animate-hero-in ${delay} motion-reduce:animate-none`;

// Eski bağlantılar (/#misyonumuz, /#indir, /#iletisim) ayrı sayfalara taşındı.
const MOVED_SECTIONS = {
  "#misyonumuz": "/misyonumuz",
  "#indir": "/mobil-ve-web",
  "#iletisim": "/topluluk",
  "#topluluk": "/topluluk",
};

export default function Home() {
  const { data: publicStats } = useGetPublicStatsQuery();
  const [statsRef, statsInView] = useInView({ threshold: 0.3 });
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const hash = location.hash;
    if (MOVED_SECTIONS[hash]) navigate(MOVED_SECTIONS[hash], { replace: true });
  }, [location.hash, navigate]);

  const stats = [
    { label: "Toplam kullanıcı", value: publicStats?.data?.totalUsers?.display, Icon: FiUser },
    { label: "Bugün aktif", value: publicStats?.data?.dailyActiveUsers?.display, Icon: FiBookOpen },
    { label: "Grup", value: publicStats?.data?.groups?.display, Icon: FiUsers },
  ];

  return (
    <main className="w-full text-terakkiInk">
      {/* Karşılama */}
      <section className="max-w-6xl mx-auto px-6 pt-4 pb-16 md:pb-24">
        <div className="grid grid-cols-1 md:grid-cols-[1.15fr_0.85fr] gap-12 items-center">
          <div className="space-y-6">
            <span className={`${enter("[animation-delay:.05s]")} inline-flex items-center gap-2 rounded-full border border-terakkiBrandMist bg-white/80 px-4 py-1.5 text-sm text-terakkiInk/70 backdrop-blur-sm`}>
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-terakkiPrimary/60 animate-ping [animation-duration:2.4s] motion-reduce:animate-none" />
                <span className="relative h-2 w-2 rounded-full bg-terakkiPrimary" />
              </span>
              Ücretsiz · iOS, Android ve web
            </span>
            <h1 className={`${enter("[animation-delay:.15s]")} text-4xl md:text-6xl font-Bold leading-[1.08] tracking-tight`}>
              Her gün bir adım,{" "}
              <span className="bg-gradient-to-r from-terakkiPrimaryDark via-terakkiPrimary to-terakkiPrimaryDark bg-[length:200%_auto] bg-clip-text text-transparent animate-hero-gradient motion-reduce:animate-none">
                ömür boyu terakki.
              </span>
            </h1>
            <p className={`${enter("[animation-delay:.3s]")} text-lg text-terakkiInk/70 leading-relaxed max-w-xl`}>
              Günlük ibadet ve okumalarını takip et, ilerlemeni gör, grubunla birlikte büyü.
              Az da olsa devamlı olanı kolaylaştırmak için tasarlandı.
            </p>
            <StoreButtons className={enter("[animation-delay:.45s]")} />
            <Link
              to="/giris"
              className={`${enter("[animation-delay:.58s]")} group inline-flex items-center gap-2 text-terakkiPrimaryDark font-SemiBold hover:text-terakkiInk transition-colors`}
            >
              ya da web'den giriş yap
              <FiArrowRight className="transition-transform group-hover:translate-x-1" />
            </Link>
            {/* Topluluk: davet doğrudan WhatsApp'a, gruplar kendi sayfasında */}
            <div className={`${enter("[animation-delay:.7s]")} flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-terakkiBrandMist pt-5`}>
              <a
                href={WHATSAPP_COMMUNITY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-3"
              >
                <img src={communityIcon} alt="" aria-hidden className="h-10 w-10 rounded-full shadow-md shadow-terakkiPrimaryDark/20" />
                <span className="leading-tight">
                  <span className="flex items-center gap-1.5 font-SemiBold text-terakkiInk group-hover:text-terakkiPrimaryDark transition-colors">
                    <FaWhatsapp className="text-[#1FA855]" aria-hidden />
                    Terakki Topluluğuna katıl
                  </span>
                  <span className="text-sm text-terakkiNeutral">Soru, öneri ve duyurular WhatsApp'ta</span>
                </span>
              </a>
              <Link to="/topluluk" className="text-sm font-SemiBold text-terakkiPrimaryDark hover:text-terakkiInk transition-colors">
                Gruplar
              </Link>
            </div>
          </div>
          <PhoneMockup />
        </div>
      </section>

      {/* Sayılar: açık şerit, çizgiyle ayrılmış */}
      <section className="border-y border-terakkiBrandMist bg-white/60 backdrop-blur">
        <div ref={statsRef} className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-terakkiBrandMist">
          {/* Görünür alana girince her öge sırayla yükselir, simge hafifçe belirir */}
          {stats.map(({ label, value, Icon }, i) => (
            <div
              key={label}
              className={`flex items-center justify-center gap-4 px-6 py-7 ${
                statsInView ? "animate-hero-in motion-reduce:animate-none" : "opacity-0 motion-reduce:opacity-100"
              }`}
              style={{ animationDelay: `${i * 0.15}s` }}
            >
              <Icon
                className={`text-terakkiPrimary ${statsInView ? "animate-pop-in motion-reduce:animate-none" : ""}`}
                style={{ animationDelay: `${i * 0.15 + 0.2}s` }}
                size={22}
              />
              <div>
                <div className="text-3xl font-SemiBold text-terakkiInk">
                  {value ? (
                    <CountUp value={value} start={statsInView} />
                  ) : (
                    <span className="inline-block h-8 w-20 animate-pulse rounded bg-terakkiBrandMist align-middle" />
                  )}
                </div>
                <div className="text-sm text-terakkiNeutral">{label}</div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
